/**
 * Kodi AI Assistant Configuration
 * Backend service endpoints for NLU, ASR, and feedback
 *
 * Configuration is loaded from environment variables (REACT_APP_*)
 * Set these in your .env file
 *
 * Authentication: Uses BFF (Backend-For-Frontend) pattern
 * - Dashboard authenticates users via HttpOnly cookies (BetterAuth session)
 * - vb-server BFF validates user and enriches context
 * - vb-server calls NLU with service-to-service X-API-Key
 * - No API keys needed in dashboard (cookie auth)
 */

import { logger } from '../utils/logger';

// Helper to get environment variable with fallback
const getEnvVar = (key: string, fallback: string): string => {
  return process.env[key] || fallback;
};

// Determine if we're in development mode
const isDevelopment = process.env.NODE_ENV === 'development';

// Build the NLU service URL from the API base URL when not explicitly set
// In production, the dashboard is on a different domain (CloudFront) than the API,
// so relative URLs won't work - we need the full API URL
const getDefaultNluService = (): string => {
  if (isDevelopment) return 'http://localhost:9004/v1/nlu/query';
  const apiUrl = process.env.REACT_APP_API_URL;
  if (apiUrl) {
    // Strip trailing slash and append the NLU query path
    return `${apiUrl.replace(/\/+$/, '')}/v1/nlu/query`;
  }
  // Last resort fallback (only works if dashboard and API share a domain)
  return '/v1/nlu/query';
};

// Debug: Log what env vars are available (only in development)
if (isDevelopment) {
  logger.info('[AssistantConfig] Environment:', {
    NODE_ENV: process.env.NODE_ENV,
    nluService: getDefaultNluService(),
  });
}

export const assistantConfig = {
  // NLU Service endpoint (BFF proxy in vb-server)
  // Authentication: HttpOnly cookies (BetterAuth session)
  // For local development: vb-server at localhost:9004
  // For production: derived from REACT_APP_API_URL (same API server)
  nluService: getEnvVar(
    'REACT_APP_NLU_SERVICE',
    getDefaultNluService()
  ),

  // Enhanced NLU (when available)
  nluEnhancedService: process.env.REACT_APP_NLU_ENHANCED_SERVICE || undefined,

  // ASR Service (if needed)
  asrService: getEnvVar(
    'REACT_APP_ASR_SERVICE',
    'https://ai1.vbeng.net/transcribe?model=vb3'
  ),

  // WebSocket service (when available)
  websocketService: process.env.REACT_APP_NLU_WEBSOCKET_SERVICE || undefined,

  // System status (when available)
  systemStatusService: process.env.REACT_APP_NLU_STATUS_SERVICE || undefined,

  // Request timeout (in milliseconds)
  // Must exceed BFF timeout (NLU_SERVICE_TIMEOUT_MS, default 90s) to avoid
  // dashboard timing out while vb-server is still waiting for NLU
  timeout: parseInt(process.env.REACT_APP_NLU_TIMEOUT || '120000', 10)
};

export default assistantConfig;

